// Login — OAuth2 PKCE entry. No password/social UI (that lives at the provider).

import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Glyph } from "@/ui";
import { Alert, Button, CardContent, CardHeader } from "@/ui";
import { pageLedeClass, pageTitleClass } from "@/components/PageHeader";
import { AuthLayout } from "@/components/AuthLayout";
import { useAuth } from "@/lib/auth";
import { toError } from "@/lib/errors";

interface LocationState {
  returnTo?: string;
}

export function Login() {
  const { status, login } = useAuth();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const returnTo = (location.state as LocationState | null)?.returnTo;

  if (status === "authenticated") {
    return <Navigate to={returnTo ?? "/"} replace />;
  }

  async function handleLogin() {
    setBusy(true);
    setError(null);
    try {
      // Redirects away on success — no need to reset `busy` then.
      await login(returnTo);
    } catch (err) {
      // e.g. sessionStorage blocked, or crypto.subtle absent on an insecure origin.
      setBusy(false);
      setError(toError(err).message);
    }
  }

  return (
    <AuthLayout
      aside={
        <p>
          Membuka dari aplikasi desktop? Login di sini menghubungkan akun yang sama — lisensi
          yang kamu aktifkan akan langsung dikenali aplikasi.
        </p>
      }
    >
      <CardHeader className="gap-2">
        <h1 className={pageTitleClass}>Masuk ke akun</h1>
        <p className={pageLedeClass}>Kelola lisensi, aktivasi perangkat, dan riwayat pembelian ahcut.</p>
      </CardHeader>
      <CardContent className="gap-4">
        <Button variant="primary" size="lg" block onClick={handleLogin} disabled={busy}>
          {busy ? (
            <Glyph name="spinner" className="motion-safe:animate-spin" />
          ) : (
            <Glyph name="logIn" />
          )}
          {busy ? "Mengalihkan…" : "Masuk"}
        </Button>
        {error && (
          <Alert tone="danger" role="alert">
            {error}
          </Alert>
        )}
        <p className="text-sm text-fg-2">
          Kamu akan diarahkan ke halaman masuk yang aman, lalu kembali ke sini.
        </p>
      </CardContent>
    </AuthLayout>
  );
}
